import { SearchOutlined } from "@ant-design/icons"
import { type InputRef, Popover, Spin, Tag, Typography } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { TextInput } from "../../components/Input/TextInput.tsx"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import {
  GLOBAL_SEARCH_MIN_LENGTH,
  type GlobalSearchGroup,
  type GlobalSearchResult,
  searchEverywhere,
  searchableGroups
} from "./global-search.ts"

const { Text } = Typography

const DEBOUNCE_MS = 250
const LISTBOX_ID = "global-search-results"

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * One search across leads, projects, customers and the archive, in the header,
 * so a name or a phone number finds the record without guessing which list it
 * is in.
 */
export function GlobalSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const permissions = useAppSelector((state) => state.auth.permissions)
  const inputRef = useRef<InputRef>(null)
  const latest = useRef(0)

  const [term, setTerm] = useState("")
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([])
  const [groupsTerm, setGroupsTerm] = useState("")
  const [active, setActive] = useState(0)

  const canSearch = searchableGroups(permissions).length > 0
  const ready = term.trim().length >= GLOBAL_SEARCH_MIN_LENGTH
  // Rows from an earlier term stay visible while the new one loads, but Enter
  // must not open one of them.
  const fresh = groupsTerm === term.trim()

  useEffect(() => {
    if (!canSearch) return
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [canSearch])

  useEffect(() => {
    if (!ready) {
      latest.current += 1
      setGroups([])
      setLoading(false)
      return
    }
    const request = ++latest.current
    setLoading(true)
    const timer = window.setTimeout(async () => {
      try {
        const found = await searchEverywhere(term, permissions)
        // An older, slower search must not paint over the newer term's rows.
        if (request !== latest.current) return
        setGroups(found)
        setGroupsTerm(term.trim())
        setActive(0)
      } finally {
        if (request === latest.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [term, ready, permissions])

  const flat = useMemo(() => groups.flatMap((group) => group.results), [groups])

  useEffect(() => {
    document
      .getElementById(`global-search-option-${active}`)
      ?.scrollIntoView?.({ block: "nearest" })
  }, [active])

  const close = () => {
    setOpen(false)
    setTerm("")
    setGroups([])
    setGroupsTerm("")
  }

  const openResult = (result: GlobalSearchResult) => {
    close()
    inputRef.current?.blur()
    navigate(result.href)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      close()
      inputRef.current?.blur()
    } else if (event.key === "ArrowDown" && flat.length) {
      event.preventDefault()
      setActive((index) => (index + 1) % flat.length)
    } else if (event.key === "ArrowUp" && flat.length) {
      event.preventDefault()
      setActive((index) => (index - 1 + flat.length) % flat.length)
    } else if (event.key === "Enter" && fresh && flat[active]) {
      event.preventDefault()
      openResult(flat[active])
    }
  }

  if (!canSearch) return null

  const noMatches =
    !loading &&
    ready &&
    groups.length > 0 &&
    flat.length === 0 &&
    groups.every((group) => !group.failed)

  let index = -1
  const content = (
    <div
      className={`max-h-[60vh] w-[420px] max-w-[90vw] overflow-y-auto ${
        fresh ? "" : "opacity-60"
      }`}
      data-testid={"global-search-results"}
    >
      {loading && !flat.length ? (
        <div className={"flex justify-center py-6"}>
          <Spin size={"small"} />
        </div>
      ) : noMatches ? (
        <Text type={"secondary"} className={"block py-4 text-center"}>
          {t("globalSearch.empty", { term: term.trim() })}
        </Text>
      ) : (
        <div id={LISTBOX_ID} role={"listbox"} className={"flex flex-col gap-3"}>
          {groups
            .filter((group) => group.results.length || group.failed)
            .map((group) => (
              <div
                key={group.key}
                role={"group"}
                aria-label={t(`globalSearch.groups.${group.key}`)}
              >
                <Text
                  type={"secondary"}
                  className={"block px-2 pb-1 text-xs uppercase"}
                >
                  {t(`globalSearch.groups.${group.key}`)}
                </Text>
                {group.failed && (
                  <Text type={"secondary"} className={"block px-2 text-xs"}>
                    {t("globalSearch.failed")}
                  </Text>
                )}
                {group.results.map((result) => {
                  index += 1
                  const selected = index === active
                  const position = index
                  return (
                    <div
                      key={result.key}
                      id={`global-search-option-${position}`}
                      role={"option"}
                      aria-selected={selected}
                      className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 ${
                        selected ? "bg-[var(--ant-control-item-bg-hover)]" : ""
                      }`}
                      onMouseEnter={() => setActive(position)}
                      onMouseDown={(event) => {
                        // Keep focus in the input until the click lands.
                        event.preventDefault()
                        openResult(result)
                      }}
                    >
                      <div className={"min-w-0 flex-1"}>
                        <div className={"truncate font-medium"}>
                          {result.title}
                        </div>
                        {result.subtitle && (
                          <Text
                            type={"secondary"}
                            className={"block truncate text-xs"}
                          >
                            {result.subtitle}
                          </Text>
                        )}
                      </div>
                      {result.archived && (
                        <Tag className={"m-0 shrink-0"}>
                          {t(`globalSearch.archived.${result.archived}`)}
                        </Tag>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  )

  return (
    <Popover
      open={open && ready}
      content={content}
      placement={"bottomRight"}
      arrow={false}
      destroyTooltipOnHide
    >
      <TextInput
        ref={inputRef}
        disableAutoCapitalize
        value={term}
        allowClear
        className={"w-72"}
        role={"combobox"}
        aria-label={t("globalSearch.label")}
        aria-expanded={open && ready}
        aria-controls={LISTBOX_ID}
        aria-activedescendant={
          flat.length ? `global-search-option-${active}` : undefined
        }
        prefix={<SearchOutlined className={"text-gray-400"} />}
        suffix={
          <Text type={"secondary"} className={"text-xs"}>
            {isMac ? "⌘K" : "Ctrl K"}
          </Text>
        }
        placeholder={t("globalSearch.placeholder")}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
    </Popover>
  )
}
